import React, { useState, useEffect } from 'react';
import { auth } from '../../../../../firebase/config';
import Modal from '../../../../../components/Modal';
import TextField from '../../../../../components/TextField';
import Button from '../../../../../components/Button';
import Toast from '../../../../../components/Toast';
import { FiUser, FiPhone, FiMail, FiBriefcase, FiDownload } from 'react-icons/fi';
import Icon from '../../../../../components/Icon';
import { submitManager, submitBulkManagers, type ManagerFormData } from '../service/addManagerService';
import * as XLSX from 'xlsx';

type AddManagerModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    bankId?: string;
};

const TYPE_OPTIONS = [
    { value: 'BranchManager', label: 'Branch Manager' },
    { value: 'OperationalManager', label: 'Operational Manager' },
    { value: 'AreaOperationManager', label: 'Area Operation Manager' },
];

export default function AddManagerModal({ isOpen, onClose, onSuccess, bankId }: AddManagerModalProps) {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [formData, setFormData] = useState<ManagerFormData>({
        name: '',
        mobile: '',
        email: '',
        type: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
        show: false,
        message: '',
        type: 'success',
    });

    useEffect(() => {
        if (!isOpen) {
            // Reset form when modal closes
            setFormData({
                name: '',
                mobile: '',
                email: '',
                type: '',
            });
            setErrors({});
        }
    }, [isOpen]);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (formData.mobile.trim() && !/^[0-9+\-\s()]+$/.test(formData.mobile.trim())) {
            newErrors.mobile = 'Please enter a valid mobile number';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.type.trim()) {
            newErrors.type = 'Type is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                Name: 'John Doe',
                Mobile: '0300-1234567',
                Email: 'john.doe@example.com',
                Type: 'BranchManager',
            },
        ];

        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Managers');
        XLSX.writeFile(wb, 'Manager_Template.xlsx');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        const uid = auth.currentUser?.uid;
        if (!uid || !bankId) {
            setErrors({ submit: 'User not authenticated or bank ID missing' });
            return;
        }

        setLoading(true);
        try {
            await submitManager(bankId, formData);

            // Reset form
            setFormData({
                name: '',
                mobile: '',
                email: '',
                type: '',
            });
            setErrors({});

            showToast('✅ Manager created successfully!', 'success');
            resetForm();
            onSuccess();
        } catch (err: any) {
            console.error('Error adding manager:', err);
            const errorMessage = err.message || 'Failed to create manager';
            showToast(`❌ ${errorMessage}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            mobile: '',
            email: '',
            type: '',
        });
        setErrors({});
    };

    const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!bankId) {
            showToast('❌ Bank ID is missing', 'error');
            return;
        }

        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);

        const reader = new FileReader();

        reader.onload = async (evt) => {
            try {
                const data = new Uint8Array(evt.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

                const managers: ManagerFormData[] = jsonData.map((row) => ({
                    name: row['Name'] || '',
                    mobile: row['Mobile'] || '',
                    email: row['Email'] || '',
                    type: row['Type'] || '',
                }));

                const result = await submitBulkManagers(bankId, managers);

                // Generate skipped rows file if any
                if (result.skippedRows.length > 0) {
                    const skippedWB = XLSX.utils.book_new();
                    const skippedWS = XLSX.utils.json_to_sheet(result.skippedRows);
                    XLSX.utils.book_append_sheet(skippedWB, skippedWS, 'SkippedRows');
                    XLSX.writeFile(skippedWB, 'SkippedManagers.xlsx');
                }

                showToast(
                    `✅ Excel upload finished. Created ${result.created} managers, skipped ${result.skipped}.`,
                    result.created > 0 ? 'success' : 'error'
                );

                if (result.created > 0) {
                    onSuccess();
                }
            } catch (err: any) {
                console.error('Error processing Excel file:', err);
                showToast(`❌ Failed to process Excel file: ${err.message}`, 'error');
            } finally {
                setUploading(false);
                // Reset file input
                e.target.value = '';
            }
        };

        reader.readAsArrayBuffer(file);
    };

    const handleClose = () => {
        setFormData({
            name: '',
            mobile: '',
            email: '',
            type: '',
        });
        setErrors({});
        onClose();
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title="Add Manager"
                size="lg"
            >
                <div className="p-6 space-y-6" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    {/* Manual Manager Entry Section */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Manual Manager Entry</h4>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <TextField
                                label="Name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="Enter Name"
                                required
                                prefix={<Icon icon={FiUser} className="text-gray-400" />}
                                className={errors.name ? 'border-red-500' : ''}
                            />
                            {errors.name && (
                                <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.name}</p>
                            )}

                            <TextField
                                label="Mobile Number (Optional)"
                                type="tel"
                                value={formData.mobile}
                                onChange={(e) => handleInputChange('mobile', e.target.value)}
                                placeholder="03xx-xxxxxxx (optional)"
                                prefix={<Icon icon={FiPhone} className="text-gray-400" />}
                                className={errors.mobile ? 'border-red-500' : ''}
                            />
                            {errors.mobile && (
                                <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.mobile}</p>
                            )}

                            <TextField
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                placeholder="Enter Email"
                                required
                                prefix={<Icon icon={FiMail} className="text-gray-400" />}
                                className={errors.email ? 'border-red-500' : ''}
                            />
                            {errors.email && (
                                <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.email}</p>
                            )}

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Manager Type <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Icon
                                        icon={FiBriefcase}
                                        className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                                    />
                                    <select
                                        value={formData.type}
                                        onChange={(e) => handleInputChange('type', e.target.value)}
                                        className={`w-full rounded-xl border py-3 pl-10 pr-4 text-sm outline-none transition bg-white text-gray-900 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${errors.type ? 'border-red-500' : ''}`}
                                        required
                                    >
                                        <option value="">Select Type</option>
                                        {TYPE_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {errors.type && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.type}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading || uploading}
                                className="w-full inline-flex items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: '#926eff', borderColor: '#926eff' }}
                                onMouseEnter={(e) => !loading && !uploading && (e.currentTarget.style.backgroundColor = '#7c5cf0')}
                                onMouseLeave={(e) => !loading && !uploading && (e.currentTarget.style.backgroundColor = '#926eff')}
                            >
                                {loading ? 'Saving...' : 'Save Manager'}
                            </button>
                        </form>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200 dark:border-gray-700"></div>

                    {/* Bulk Upload via Excel Section */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Bulk Upload via Excel</h4>

                        <button
                            type="button"
                            onClick={downloadTemplate}
                            disabled={uploading}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
                            onMouseEnter={(e) => !uploading && (e.currentTarget.style.backgroundColor = '#059669')}
                            onMouseLeave={(e) => !uploading && (e.currentTarget.style.backgroundColor = '#10b981')}
                        >
                            <Icon icon={FiDownload} className="h-4 w-4" />
                            Download Template
                        </button>

                        <div>
                            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Upload Excel File
                            </label>
                            <div className="relative">
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleExcelUpload}
                                    disabled={uploading || loading}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-300"
                                />
                            </div>
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                Use the downloaded template to ensure correct format
                            </p>
                            {uploading && (
                                <p className="mt-2 text-sm text-blue-600 dark:text-blue-400">Processing Excel file...</p>
                            )}
                        </div>
                    </div>

                    {/* Close Button */}
                    <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleClose}
                            disabled={loading || uploading}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Toast Notification */}
            {toast.show && (
                <div className="fixed top-4 right-4 z-[9999]">
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast({ ...toast, show: false })}
                    />
                </div>
            )}
        </>
    );
}

